import { toast } from "sonner";

import { routes } from "~/core/constants/routes";

import ObjectRecordCreateSonner from "~/components/ObjectRecordCreateSonner";

import { useGlobalNewOpportunityModalContext } from "../context";
import { useGlobalCreateOpportunityStore } from "../store";
import { OpportunityFormValues } from "../types";
import { formatDateValues } from "../utils";

import { useCreateOpportunityMutation } from "./useCreateOpportunityMutation";

const toastId = "global-new-opportunity-modal";

export const useOpportunityForm = () => {
  const { form, oppNameRef } = useGlobalNewOpportunityModalContext();
  const setIsOpenGlobalCreateOpportunity = useGlobalCreateOpportunityStore(
    state => state.setIsOpenGlobalCreateOpportunity,
  );
  const isSaveAndNewMode = useGlobalCreateOpportunityStore(
    state => state.isSaveAndNewMode,
  );
  const setIsSaveAndNewMode = useGlobalCreateOpportunityStore(
    state => state.setIsSaveAndNewMode,
  );
  const setIsOpportunityCreating = useGlobalCreateOpportunityStore(
    state => state.setIsOpportunityCreating,
  );
  const resetSelection = useGlobalCreateOpportunityStore(
    state => state.resetSelection,
  );
  const resetActiveStateOfSelectTypeAttributes =
    useGlobalCreateOpportunityStore(
      state => state.resetActiveStateOfSelectTypeAttributes,
    );
  const resetAllStates = useGlobalCreateOpportunityStore(
    state => state.resetAllStates,
  );

  const { createOpportunity } = useCreateOpportunityMutation({
    onSuccess: (opportunity, opportunityData) => {
      if (!isSaveAndNewMode) {
        setIsOpenGlobalCreateOpportunity(false);
        setIsSaveAndNewMode(false);
      } else {
        setTimeout(() => {
          oppNameRef?.current?.focus();
        }, 100);
        // Scroll to oppNameRef input
        oppNameRef?.current?.input?.scrollIntoView({ behavior: "smooth" });
      }

      toast(
        <ObjectRecordCreateSonner
          toastId={toastId}
          title="Opportunity created"
          viewButtonLabel="Open opportunity"
          description={opportunityData.opportunity_name}
          href={routes.opportunity.detailsPage(opportunity.opportunity_id)}
        />,
        {
          id: toastId,
        },
      );

      resetSelection();
      resetActiveStateOfSelectTypeAttributes();
      form?.resetFields();
      setIsOpportunityCreating(false);
    },
    onError: error => {
      toast.error("Error creating opportunity");
      setIsOpportunityCreating(false);
    },
  });

  const onFinish = async (
    values: OpportunityFormValues & Record<string, unknown>,
  ) => {
    setIsOpportunityCreating(true);
    const {
      opportunity_account,
      opportunity_name,
      opportunity_deal_size,
      opportunity_stage,
      opportunity_close_date,
      opportunity_contact,
      opportunity_other_stakeholders,
      opportunity_next_step,
      opportunity_owner,
      opportunity_next_action_date,
      ...rest
    } = values;
    const formattedValues = formatDateValues(rest);

    const iso_close_date = opportunity_close_date
      ? new Date(opportunity_close_date)?.toISOString()
      : undefined;
    const iso_next_action_date = opportunity_next_action_date
      ? new Date(opportunity_next_action_date)?.toISOString()
      : undefined;

    await createOpportunity({
      opportunity_account,
      opportunity_name,
      opportunity_deal_size,
      opportunity_stage,
      opportunity_close_date: iso_close_date,
      opportunity_contact,
      opportunity_other_stakeholders,
      opportunity_next_step,
      opportunity_owner,
      opportunity_next_action_date: iso_next_action_date,
      ...formattedValues,
    });

    resetAllStates();
  };

  return { onFinish };
};
